import '../styles/ShipmentsRegistry.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { projectData, useProjectCollection } from '../lib/projectData';

const SHIPMENTS_KEY = 'consignment_shipments';
const CUSTODY_KEY = 'custody_events';
const ACCOUNTS_KEY = 'accounts';

const STAGES = ['Picked up', 'In transit', 'Customs', 'Out for delivery', 'Delivered'];
const CHAIN_STAGES = ['Picked up', 'In transit', 'Customs', 'Out for delivery', 'Delivered'];

const FIELD_LABELS = {
  waybillNumber: 'Waybill number',
  origin: 'Origin',
  destination: 'Destination',
  weight: 'Weight',
  dimensions: 'Dimensions',
  declaredContents: 'Declared contents',
  estimatedDelivery: 'Estimated delivery',
  senderEmail: 'Sender account',
  recipientEmail: 'Recipient account',
};

const EMPTY_DRAFT = {
  waybillNumber: '',
  origin: '',
  destination: '',
  weight: '',
  dimensions: '',
  declaredContents: '',
  estimatedDelivery: '',
  senderEmail: '',
  recipientEmail: '',
};

function stageIndex(stage) {
  const i = CHAIN_STAGES.indexOf(stage);
  return i < 0 ? 0 : i;
}

function stageAngle(stage) {
  return stageIndex(stage) * 60 - 120;
}

function isDelivered(stage) {
  return stage === 'Delivered';
}

function nowStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return (
    d.getFullYear() +
    '-' +
    pad(d.getMonth() + 1) +
    '-' +
    pad(d.getDate()) +
    ' ' +
    pad(d.getHours()) +
    ':' +
    pad(d.getMinutes())
  );
}

function nextStage(stage) {
  const i = stageIndex(stage);
  return i < CHAIN_STAGES.length - 1 ? CHAIN_STAGES[i + 1] : null;
}

function validateDraft(draft) {
  const errors = {};
  if (!String(draft.waybillNumber || '').trim()) {
    errors.waybillNumber = 'A waybill number is required to identify this consignment.';
  }
  if (!String(draft.origin || '').trim()) {
    errors.origin = 'Enter the origin the goods are collected from.';
  }
  if (!String(draft.destination || '').trim()) {
    errors.destination = 'Enter the destination the goods are delivered to.';
  }
  if (!String(draft.weight || '').trim()) {
    errors.weight = 'Enter the shipment weight, for example 18.4 kg.';
  }
  if (!String(draft.dimensions || '').trim()) {
    errors.dimensions = 'Enter the crate dimensions, for example 60x40x35 cm.';
  }
  if (!String(draft.declaredContents || '').trim()) {
    errors.declaredContents = 'Describe the declared contents of the consignment.';
  }
  if (!String(draft.estimatedDelivery || '').trim()) {
    errors.estimatedDelivery = 'Set the estimated delivery date.';
  }
  if (!String(draft.senderEmail || '').trim()) {
    errors.senderEmail = 'Bind the consignment to a sender account.';
  }
  return errors;
}

/* ---------- one-shot tabular count-up ---------- */

function CountUp({ value }) {
  const target = useMemo(() => {
    const digits = String(value == null ? '' : value).replace(/[^0-9]/g, '');
    return digits ? parseInt(digits, 10) : NaN;
  }, [value]);
  const parts = useMemo(() => {
    const raw = String(value == null ? '' : value);
    const i = raw.search(/[0-9]/);
    if (i < 0) return { prefix: raw, suffix: '' };
    let j = i;
    while (j < raw.length && /[0-9]/.test(raw[j])) j += 1;
    return { prefix: raw.slice(0, i), suffix: raw.slice(j) };
  }, [value]);
  const [display, setDisplay] = useState(parts.prefix + (Number.isNaN(target) ? '' : target) + parts.suffix);
  const done = useRef(false);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (Number.isNaN(target) || reduce) {
      done.current = true;
      setDisplay(parts.prefix + (Number.isNaN(target) ? '' : target) + parts.suffix);
      return undefined;
    }
    if (done.current) return undefined;
    let raf = 0;
    const start = performance.now();
    const duration = 620;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(parts.prefix + Math.round(target * eased) + parts.suffix);
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        done.current = true;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, parts.prefix, parts.suffix]);

  return <span>{display}</span>;
}

/* ---------- circular status gauge ---------- */

function StatusGauge({ stage }) {
  const angle = stageAngle(stage);
  const done = isDelivered(stage);
  return (
    <div className={'srg-gauge' + (done ? ' srg-gauge--teal' : '')}>
      <svg viewBox="0 0 64 64" role="img" aria-label={'Shipment stage: ' + stage}>
        <path className="srg-gauge-track" d="M32 8 A24 24 0 0 1 32 56" />
        <path className="srg-gauge-arc" d="M32 8 A24 24 0 0 1 32 56" />
        <circle className="srg-gauge-hub" cx="32" cy="32" r="4" />
        <g
          className="srg-gauge-needle"
          style={{ '--srg-angle': angle + 'deg' }}
        >
          <line x1="32" y1="32" x2="32" y2="13" />
        </g>
      </svg>
    </div>
  );
}

/* ---------- shared field ---------- */

function Field({ name, draft, onChange, errors, type, placeholder, options, full }) {
  const err = errors[name];
  const isSelect = Array.isArray(options);
  const cls =
    'srg-field' +
    (full ? ' srg-f-wide' : ' srg-f-half') +
    (err ? ' has-error' : '');
  return (
    <div className={cls}>
      <label htmlFor={'srg-' + name}>{FIELD_LABELS[name] || name}</label>
      {isSelect ? (
        <select
          id={'srg-' + name}
          name={name}
          value={draft[name]}
          onChange={onChange}
          aria-invalid={err ? 'true' : undefined}
          aria-describedby={err ? 'srg-err-' + name : undefined}
        >
          <option value="">Select an account</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={'srg-' + name}
          name={name}
          type={type || 'text'}
          value={draft[name]}
          onChange={onChange}
          placeholder={placeholder}
          aria-invalid={err ? 'true' : undefined}
          aria-describedby={err ? 'srg-err-' + name : undefined}
        />
      )}
      {err ? (
        <span className="srg-fielderr" id={'srg-err-' + name}>
          {err}
        </span>
      ) : null}
    </div>
  );
}

/* ============================================================ */

function ShipmentsRegistry() {
  const {
    data: shipments,
    loading,
    error,
    refresh,
  } = useProjectCollection(SHIPMENTS_KEY);

  const { data: custodyEvents } = useProjectCollection(CUSTODY_KEY);
  const { data: accounts } = useProjectCollection(ACCOUNTS_KEY);

  const rows = Array.isArray(shipments) ? shipments : [];
  const allCustody = Array.isArray(custodyEvents) ? custodyEvents : [];
  const allAccounts = Array.isArray(accounts) ? accounts : [];

  const [selectedId, setSelectedId] = useState(null);
  const [consoleMode, setConsoleMode] = useState(null); // 'create' | 'edit' | null
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveStatus, setSaveStatus] = useState({ state: 'idle', message: '' });
  const [advanceStatus, setAdvanceStatus] = useState({ state: 'idle', message: '' });
  const [handoffStatus, setHandoffStatus] = useState({ state: 'idle', message: '' });

  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) || null,
    [rows, selectedId]
  );

  const custodyFor = useCallback(
    (shipmentId) =>
      allCustody
        .filter((e) => e.shipmentId === shipmentId)
        .slice()
        .sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp))),
    [allCustody]
  );

  const accountOptions = useMemo(
    () =>
      allAccounts
        .filter((a) => a.role === 'sender' || a.role === 'recipient')
        .map((a) => ({
          value: a.email,
          label: (a.displayName ? a.displayName + ' — ' : '') + a.email,
          role: a.role,
        })),
    [allAccounts]
  );

  const senderOptions = accountOptions.filter((o) => o.role === 'sender');
  const recipientOptions = accountOptions.filter((o) => o.role === 'recipient');

  const openCreate = () => {
    setConsoleMode('create');
    setDraft(EMPTY_DRAFT);
    setFieldErrors({});
    setSaveStatus({ state: 'idle', message: '' });
  };

  const openEdit = (record, { scroll = true } = {}) => {
    setSelectedId(record.id);
    setConsoleMode('edit');
    setDraft({
      waybillNumber: record.waybillNumber || '',
      origin: record.origin || '',
      destination: record.destination || '',
      weight: record.weight || '',
      dimensions: record.dimensions || '',
      declaredContents: record.declaredContents || '',
      estimatedDelivery: record.estimatedDelivery || '',
      senderEmail: record.senderEmail || '',
      recipientEmail: record.recipientEmail || '',
    });
    setFieldErrors({});
    setSaveStatus({ state: 'idle', message: '' });
    setAdvanceStatus({ state: 'idle', message: '' });
    setHandoffStatus({ state: 'idle', message: '' });
    if (scroll && typeof document !== 'undefined') {
      const panel = document.getElementById('srg-console');
      if (panel && panel.scrollIntoView) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  };

  const closeConsole = () => {
    setConsoleMode(null);
    setDraft(EMPTY_DRAFT);
    setFieldErrors({});
    setSaveStatus({ state: 'idle', message: '' });
  };

  const onField = (e) => {
    const { name, value } = e.target;
    setDraft((d) => ({ ...d, [name]: value }));
    setFieldErrors((f) => {
      if (!f[name]) return f;
      const next = { ...f };
      delete next[name];
      return next;
    });
  };

  const recordToDraft = (record) => ({
    waybillNumber: record.waybillNumber,
    origin: record.origin,
    destination: record.destination,
    weight: record.weight,
    dimensions: record.dimensions,
    declaredContents: record.declaredContents,
    estimatedDelivery: record.estimatedDelivery,
    senderEmail: record.senderEmail,
    recipientEmail: record.recipientEmail,
  });

  const doSave = async () => {
    const errors = validateDraft(draft);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSaveStatus({
        state: 'error',
        message: 'The consignment was not saved. Correct the highlighted field(s) and save again.',
      });
      return;
    }
    setSaveStatus({ state: 'saving', message: '' });
    try {
      if (consoleMode === 'create') {
        const record = {
          id: draft.waybillNumber.trim(),
          createdAt: nowStamp().slice(0, 10),
          waybillNumber: draft.waybillNumber.trim(),
          origin: draft.origin.trim(),
          destination: draft.destination.trim(),
          weight: draft.weight.trim(),
          dimensions: draft.dimensions.trim(),
          declaredContents: draft.declaredContents.trim(),
          stage: CHAIN_STAGES[0],
          estimatedDelivery: draft.estimatedDelivery.trim(),
          senderEmail: draft.senderEmail,
          recipientEmail: draft.recipientEmail,
        };
        await projectData.create(SHIPMENTS_KEY, record);
        await projectData.create(CUSTODY_KEY, {
          id: 'ce-' + record.id + '-1',
          shipmentId: record.id,
          stage: CHAIN_STAGES[0],
          timestamp: nowStamp(),
          actor: 'Administrator',
        });
        setSelectedId(record.id);
        setConsoleMode('edit');
        setSaveStatus({ state: 'ok', message: 'Consignment ' + record.id + ' created.' });
      } else if (consoleMode === 'edit' && selectedId) {
        await projectData.update(SHIPMENTS_KEY, selectedId, recordToDraft({
          ...draft,
          waybillNumber: draft.waybillNumber.trim(),
          origin: draft.origin.trim(),
          destination: draft.destination.trim(),
          weight: draft.weight.trim(),
          dimensions: draft.dimensions.trim(),
          declaredContents: draft.declaredContents.trim(),
          estimatedDelivery: draft.estimatedDelivery.trim(),
        }));
        setSaveStatus({ state: 'ok', message: 'Changes to ' + selectedId + ' saved.' });
      }
      if (typeof refresh === 'function') refresh();
    } catch (err) {
      setSaveStatus({
        state: 'error',
        message: 'Save failed: ' + ((err && err.message) || 'the registry did not accept the change'),
      });
    }
  };

  const advanceTo = async (stage) => {
    if (!selected || stageIndex(stage) <= stageIndex(selected.stage)) return;
    setAdvanceStatus({ state: 'saving', message: '' });
    try {
      await projectData.update(SHIPMENTS_KEY, selected.id, { stage });
      await projectData.create(CUSTODY_KEY, {
        id: 'ce-' + selected.id + '-' + stageIndex(stage) + '-' + Date.now(),
        shipmentId: selected.id,
        stage,
        timestamp: nowStamp(),
        actor: 'Administrator',
      });
      setAdvanceStatus({ state: 'ok', message: 'Stage advanced to ' + stage + '.' });
      if (typeof refresh === 'function') refresh();
    } catch (err) {
      setAdvanceStatus({
        state: 'error',
        message:
          'Stage advance to ' + stage + ' failed: ' +
          ((err && err.message) || 'the registry did not accept the change') +
          '. The row is unchanged.',
      });
    }
  };

  const recordHandoff = async () => {
    if (!selected) return;
    setHandoffStatus({ state: 'saving', message: '' });
    try {
      await projectData.create(CUSTODY_KEY, {
        id: 'ce-' + selected.id + '-h-' + Date.now(),
        shipmentId: selected.id,
        stage: selected.stage,
        timestamp: nowStamp(),
        actor: 'Administrator',
      });
      setHandoffStatus({ state: 'ok', message: 'Custody handoff recorded at ' + selected.stage + '.' });
      if (typeof refresh === 'function') refresh();
    } catch (err) {
      setHandoffStatus({
        state: 'error',
        message:
          'Recording the custody handoff failed: ' +
          ((err && err.message) || 'the registry did not accept the event') +
          '. Nothing was appended.',
      });
    }
  };

  const retryList = () => {
    if (typeof refresh === 'function') refresh();
  };

  const retrySave = () => doSave();

  const showConsole = consoleMode !== null;
  const emptyState = !loading && !error && rows.length === 0;

  const consoleTitle =
    consoleMode === 'create'
      ? 'New consignment'
      : selected
      ? 'Waybill ' + selected.waybillNumber
      : 'Consignment';

  return (
    <section className="srg-root" aria-label="Consignment shipment registry">
      <div className="srg-shell">
        {/* ---------- workspace head ---------- */}
        <header className="srg-head">
          <div className="srg-head-main">
            <span className="srg-eyebrow">Administrator workspace · Consignment registry</span>
            <h2 className="srg-title">Consignment Registry</h2>
            <p className="srg-meta">
              <span>
                Consignments on file <b>{String(rows.length).padStart(2, '0')}</b>
              </span>
              <span>Custody stages <b>{CHAIN_STAGES.length}</b></span>
            </p>
          </div>
          <div className="srg-head-actions">
            <button
              type="button"
              className="srg-btn srg-btn--primary"
              aria-expanded={showConsole}
              aria-controls="srg-console"
              onClick={() => (consoleMode === 'create' ? closeConsole() : openCreate())}
            >
              {consoleMode === 'create' ? 'Close console' : 'New consignment'}
            </button>
          </div>
        </header>

        {/* ---------- list error ---------- */}
        {error ? (
          <div className="srg-errorband">
            <p>
              The shipment registry could not be loaded:{' '}
              {(error && error.message) || 'the registry did not respond'}.
            </p>
            <button type="button" className="srg-btn" onClick={retryList}>
              Retry list load
            </button>
          </div>
        ) : null}

        {/* ---------- ruled row list ---------- */}
        <div className="srg-list-wrap">
          <div className="srg-colhead" aria-hidden="true">
            <span className="srg-c-waybill">Waybill</span>
            <span className="srg-c-route">Route</span>
            <span className="srg-c-mass">Weight · Dimensions</span>
            <span className="srg-c-gauge">Stage</span>
          </div>

          {loading ? (
            <div aria-hidden="true">
              <div className="srg-placeholder">
                <div className="srg-ph srg-c-waybill" />
                <div className="srg-ph srg-c-route" />
                <div className="srg-ph srg-c-mass" />
                <div className="srg-ph srg-ph--gauge srg-c-gauge" />
              </div>
              <div className="srg-placeholder">
                <div className="srg-ph srg-c-waybill" />
                <div className="srg-ph srg-c-route" />
                <div className="srg-ph srg-c-mass" />
                <div className="srg-ph srg-ph--gauge srg-c-gauge" />
              </div>
              <p className="srg-sr" role="status">
                Loading the shipment registry.
              </p>
            </div>
          ) : emptyState ? (
            <div className="srg-empty">
              <div className="srg-empty-copy">
                <h3>No consignments on file</h3>
                <p>
                  The registry holds no shipment rows yet. Create the first consignment to issue a
                  waybill number and open its custody chain.
                </p>
              </div>
              <button
                type="button"
                className="srg-btn srg-btn--primary"
                aria-expanded={showConsole}
                aria-controls="srg-console"
                onClick={openCreate}
              >
                New consignment
              </button>
            </div>
          ) : (
            <ul className="srg-list">
              {rows.map((row) => {
                const open = row.id === selectedId;
                const delivered = isDelivered(row.stage);
                const timeline = custodyFor(row.id).slice(0, 4);
                const rowSaveErr =
                  open && saveStatus.state === 'error' ? saveStatus.message : '';
                const rowAdvErr =
                  open && advanceStatus.state === 'error' ? advanceStatus.message : '';
                const rowHandErr =
                  open && handoffStatus.state === 'error' ? handoffStatus.message : '';
                return (
                  <li key={row.id} className={'srg-item' + (open ? ' is-open' : '')}>
                    <div className="srg-row">
                      <button
                        type="button"
                        className="srg-row-btn"
                        aria-expanded={open}
                        aria-controls={'srg-rowpanel-' + row.id}
                        onClick={() => {
                          if (open && consoleMode === 'edit') {
                            setSelectedId(null);
                            setConsoleMode(null);
                          } else {
                            openEdit(row, { scroll: false });
                            const panel = document.getElementById('srg-console');
                            if (panel && panel.scrollIntoView) {
                              panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            }
                          }
                        }}
                      >
                        <span className="srg-cell srg-c-waybill">
                          <span className="srg-label">Waybill</span>
                          <span className="srg-waybill">
                            <CountUp value={row.waybillNumber} />
                          </span>
                          <span className="srg-created">Opened {row.createdAt}</span>
                        </span>
                        <span className="srg-cell srg-c-route">
                          <span className="srg-label">Route</span>
                          <span className="srg-route">
                            <span>{row.origin}</span>
                            <span className="srg-arrow" aria-hidden="true">
                              &rarr;
                            </span>
                            <span>{row.destination}</span>
                          </span>
                          <span className="srg-contents">{row.declaredContents}</span>
                        </span>
                        <span className="srg-cell srg-c-mass">
                          <span className="srg-label">Weight · Dimensions</span>
                          <span className="srg-massline">
                            <span>
                              <CountUp value={row.weight} />
                            </span>
                            <span>{row.dimensions}</span>
                          </span>
                          <span className="srg-eta">ETA {row.estimatedDelivery}</span>
                        </span>
                      </button>

                      <div className="srg-gaugecell">
                        <StatusGauge stage={row.stage} />
                        <span className="srg-stagelabel">{row.stage}</span>
                      </div>
                    </div>

                    {open ? (
                      <div id={'srg-rowpanel-' + row.id} className="srg-rowpanel">
                        {rowSaveErr || rowAdvErr || rowHandErr ? (
                          <div className="srg-rowmsg is-error">
                            <p>
                              {rowSaveErr || rowAdvErr || rowHandErr}{' '}
                              The row is held at its last confirmed state; no data was lost.
                            </p>
                            <button
                              type="button"
                              className="srg-btn"
                              onClick={() => {
                                if (rowSaveErr) retrySave();
                                else if (rowAdvErr) advanceTo(nextStage(row.stage));
                                else recordHandoff();
                              }}
                            >
                              Retry
                            </button>
                          </div>
                        ) : null}

                        <div className="srg-rowmsg srg-rowmsg--timeline">
                          <p>Recent custody</p>
                        </div>
                        <ul className="srg-rowtimeline">
                          {timeline.length > 0 ? (
                            timeline.map((ev) => (
                              <li key={ev.id} className="srg-rowtl-item">
                                <span
                                  className={
                                    'srg-hist-stage' +
                                    (isDelivered(ev.stage) ? ' is-delivered' : '')
                                  }
                                >
                                  {ev.stage}
                                </span>
                                <span
                                  className={
                                    'srg-hist-time' +
                                    (isDelivered(ev.stage) ? ' is-delivered' : '')
                                  }
                                >
                                  {ev.timestamp}
                                </span>
                                <span className="srg-hist-actor">{ev.actor}</span>
                              </li>
                            ))
                          ) : (
                            <li className="srg-rowtl-item">
                              <span className="srg-empty-note">No custody events recorded yet.</span>
                            </li>
                          )}
                        </ul>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ---------- create / edit console ---------- */}
        {showConsole ? (
          <div className="srg-console" id="srg-console">
            <div className="srg-console-head">
              <h3 className="srg-console-title">{consoleTitle}</h3>
              <span className="srg-console-sub">
                {consoleMode === 'create'
                  ? 'New waybill · Picked up stage assigned on save'
                  : 'Editing bound accounts and shipment details'}
              </span>
            </div>

            {/* --- details --- */}
            <div className="srg-section">
              <span className="srg-section-label">Consignment details</span>
              <div className="srg-fields">
                <Field
                  name="waybillNumber"
                  draft={draft}
                  onChange={onField}
                  errors={fieldErrors}
                  placeholder="FFD-0000-0000"
                />
                <Field
                  name="estimatedDelivery"
                  draft={draft}
                  onChange={onField}
                  errors={fieldErrors}
                  type="date"
                />
                <Field
                  name="origin"
                  draft={draft}
                  onChange={onField}
                  errors={fieldErrors}
                  placeholder="Origin city or port"
                />
                <Field
                  name="destination"
                  draft={draft}
                  onChange={onField}
                  errors={fieldErrors}
                  placeholder="Destination city or port"
                />
                <Field
                  name="weight"
                  draft={draft}
                  onChange={onField}
                  errors={fieldErrors}
                  placeholder="18.4 kg"
                />
                <Field
                  name="dimensions"
                  draft={draft}
                  onChange={onField}
                  errors={fieldErrors}
                  placeholder="60x40x35 cm"
                />
                <Field
                  name="declaredContents"
                  draft={draft}
                  onChange={onField}
                  errors={fieldErrors}
                  placeholder="What the consignment contains"
                  full
                />
                <Field
                  name="senderEmail"
                  draft={draft}
                  onChange={onField}
                  errors={fieldErrors}
                  options={senderOptions}
                />
                <Field
                  name="recipientEmail"
                  draft={draft}
                  onChange={onField}
                  errors={fieldErrors}
                  options={recipientOptions}
                />
              </div>

              <div className="srg-console-actions">
                <button
                  type="button"
                  className="srg-btn srg-btn--primary"
                  onClick={doSave}
                  disabled={saveStatus.state === 'saving'}
                >
                  {saveStatus.state === 'saving'
                    ? 'Saving…'
                    : consoleMode === 'create'
                    ? 'Create consignment'
                    : 'Save changes'}
                </button>
                <button type="button" className="srg-btn" onClick={closeConsole}>
                  Close
                </button>
                {saveStatus.state !== 'idle' && saveStatus.state !== 'saving' ? (
                  <p
                    className={
                      'srg-msg' + (saveStatus.state === 'error' ? ' is-error' : ' is-ok')
                    }
                    role="status"
                  >
                    {saveStatus.message}
                    {saveStatus.state === 'error' ? ' ' : ''}
                  </p>
                ) : null}
                {saveStatus.state === 'error' ? (
                  <button type="button" className="srg-btn srg-btn--quiet" onClick={retrySave}>
                    Retry save
                  </button>
                ) : null}
              </div>
            </div>

            {/* --- stage + handoff --- */}
            {consoleMode === 'edit' && selected ? (
              <div className="srg-section">
                <span className="srg-section-label">Custody chain — advance stage</span>
                <div className="srg-stages">
                  {CHAIN_STAGES.map((st, i) => {
                    const cur = stageIndex(selected.stage) === i;
                    const done = stageIndex(selected.stage) > i;
                    return (
                      <button
                        key={st}
                        type="button"
                        className={
                          'srg-stage' +
                          (cur ? ' is-current' : '') +
                          (done ? ' is-done' : '')
                        }
                        disabled={i <= stageIndex(selected.stage) || advanceStatus.state === 'saving'}
                        aria-current={cur ? 'step' : undefined}
                        onClick={() => advanceTo(st)}
                      >
                        <span className="srg-stage-mark" aria-hidden="true" />
                        <span>{st}</span>
                        <span className="srg-stage-index">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="srg-console-actions">
                  <button
                    type="button"
                    className="srg-btn"
                    onClick={recordHandoff}
                    disabled={handoffStatus.state === 'saving'}
                  >
                    {handoffStatus.state === 'saving'
                      ? 'Recording…'
                      : 'Record custody handoff'}
                  </button>
                  {advanceStatus.state !== 'idle' && advanceStatus.state !== 'saving' ? (
                    <p
                      className={
                        'srg-msg' + (advanceStatus.state === 'error' ? ' is-error' : ' is-ok')
                      }
                      role="status"
                    >
                      {advanceStatus.message}
                    </p>
                  ) : null}
                  {advanceStatus.state === 'error' ? (
                    <button
                      type="button"
                      className="srg-btn srg-btn--quiet"
                      onClick={() => advanceTo(nextStage(selected.stage))}
                    >
                      Retry advance
                    </button>
                  ) : null}
                  {handoffStatus.state !== 'idle' && handoffStatus.state !== 'saving' ? (
                    <p
                      className={
                        'srg-msg' + (handoffStatus.state === 'error' ? ' is-error' : ' is-ok')
                      }
                      role="status"
                    >
                      {handoffStatus.message}
                    </p>
                  ) : null}
                  {handoffStatus.state === 'error' ? (
                    <button
                      type="button"
                      className="srg-btn srg-btn--quiet"
                      onClick={recordHandoff}
                    >
                      Retry handoff
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* --- custody history --- */}
            {consoleMode === 'edit' && selected ? (
              <div className="srg-section">
                <span className="srg-section-label">
                  Custody history — waybill {selected.waybillNumber}
                </span>
                <div className="srg-history-head" aria-hidden="true">
                  <span>Stage</span>
                  <span>Timestamp</span>
                  <span>Actor</span>
                </div>
                <ul className="srg-history">
                  {custodyFor(selected.id).length > 0 ? (
                    custodyFor(selected.id).map((ev) => (
                      <li key={ev.id} className="srg-history-row">
                        <span
                          className={
                            'srg-hist-stage' + (isDelivered(ev.stage) ? ' is-delivered' : '')
                          }
                        >
                          {ev.stage}
                        </span>
                        <span
                          className={
                            'srg-hist-time' + (isDelivered(ev.stage) ? ' is-delivered' : '')
                          }
                        >
                          {ev.timestamp}
                        </span>
                        <span className="srg-hist-actor">{ev.actor}</span>
                      </li>
                    ))
                  ) : (
                    <li className="srg-history-row">
                      <span className="srg-empty-note">
                        No custody events recorded for this consignment yet.
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default ShipmentsRegistry;